// Guide §11.5 — stock rules (pure parts).
import { D, Decimal, money, round2, ZERO } from './money';
import type { MoneyInput } from './money';

/**
 * Average cost on receipt: new = (max(onHand,0) × oldAvg + qty × price) ÷ (max(onHand,0) + qty).
 * onHand ≤ 0 → the new price is the average.
 */
export function averageCost(onHand: MoneyInput, oldAvg: MoneyInput, qty: MoneyInput, unitCost: MoneyInput): Decimal {
  const base = Decimal.max(D(onHand), ZERO);
  const q = D(qty);
  const denom = base.plus(q);
  if (denom.lte(0)) return money(unitCost);
  if (base.isZero()) return money(unitCost);
  return money(base.times(oldAvg).plus(q.times(unitCost)).dividedBy(denom));
}

/** Count adjustment: movement = counted − onHand */
export const countAdjustment = (counted: MoneyInput, onHand: MoneyInput): Decimal => D(counted).minus(onHand);

/** Base-unit quantity for a sale in another unit */
export const baseQty = (qty: MoneyInput, unitFactor: MoneyInput = 1): Decimal => D(qty).times(unitFactor);

/** Stock value for reports/Zakat */
export const stockValue = (onHand: MoneyInput, price: MoneyInput): Decimal => round2(Decimal.max(D(onHand), ZERO).times(price));

export interface BatchLike {
  id: string;
  onHand: MoneyInput;
  expiryDate?: string | null;
}
/** FEFO: earliest expiry first; batches without expiry last (Guide §11.5). Only batches with stock. */
export function fefoOrder<T extends BatchLike>(batches: T[]): T[] {
  return batches
    .filter((b) => D(b.onHand).gt(0))
    .sort((a, b) => (a.expiryDate ?? '9999-12-31').localeCompare(b.expiryDate ?? '9999-12-31'));
}

/** Split a sale quantity over FEFO batches. Remainder (if stock is short) is returned as `short`. */
export function pickBatches<T extends BatchLike>(batches: T[], need: MoneyInput): { picks: Array<{ batch: T; qty: Decimal }>; short: Decimal } {
  let left = D(need);
  const picks: Array<{ batch: T; qty: Decimal }> = [];
  for (const b of fefoOrder(batches)) {
    if (left.lte(0)) break;
    const take = Decimal.min(left, D(b.onHand));
    picks.push({ batch: b, qty: take });
    left = left.minus(take);
  }
  return { picks, short: Decimal.max(left, ZERO) };
}

export type ExpiryState = 'expired' | 'soon' | 'ok' | 'none';
export function expiryState(expiryDate: string | null | undefined, today: string, soonDays = 30): ExpiryState {
  if (!expiryDate) return 'none';
  if (expiryDate < today) return 'expired';
  const diff = Math.round((Date.parse(expiryDate) - Date.parse(today)) / 86_400_000);
  return diff <= soonDays ? 'soon' : 'ok';
}

export const isLowStock = (onHand: MoneyInput, reorderLevel: MoneyInput | null | undefined): boolean =>
  reorderLevel !== null && reorderLevel !== undefined && D(onHand).lte(reorderLevel);
